import { useEffect } from "react";
import { useFrappeAuth } from "frappe-react-sdk";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import {
  getServiceWorkerRegistration,
  registerOwnerFlowServiceWorker,
} from "../lib/registerServiceWorker";
import { isPushSupported, ownerFlowPushNotification } from "../lib/pushNotifications";
import {
  ADMIN_NOTIFICATIONS_PATH,
  isAdminPath,
  isOwnerPath,
  MANAGER_NOTIFICATIONS_PATH,
  OWNER_NOTIFICATIONS_PATH,
} from "../lib/portal";
import { resolveNotificationPath } from "../features/notifications/useNotifications";
import { showNotificationToast } from "../features/notifications/showNotificationToast";

export function PushNotificationSync() {
  const { t } = useTranslation();
  const { currentUser } = useFrappeAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ownerPortal = isOwnerPath(location.pathname);
  const adminPortal = isAdminPath(location.pathname);
  const listPath = adminPortal
    ? ADMIN_NOTIFICATIONS_PATH
    : ownerPortal
      ? OWNER_NOTIFICATIONS_PATH
      : MANAGER_NOTIFICATIONS_PATH;

  useEffect(() => {
    if (!currentUser || currentUser === "Guest") return;
    registerOwnerFlowServiceWorker();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || currentUser === "Guest" || !isPushSupported()) return;

    let cancelled = false;

    void (async () => {
      const registration = await getServiceWorkerRegistration();
      if (!registration || cancelled) return;

      try {
        await ownerFlowPushNotification.initialize(registration);
        ownerFlowPushNotification.onMessage((payload) => {
          const title = payload.notification?.title || payload.data?.title || t("notifications.title");
          const body =
            payload.notification?.body || payload.data?.body || t("notifications.newReceived");
          const rawLink = payload.data?.url || payload.data?.link;
          const path = resolveNotificationPath(
            typeof rawLink === "string" ? rawLink : undefined,
            undefined,
            undefined,
            ownerPortal,
          );

          showNotificationToast({
            id: "ownerflow-push",
            title: String(title),
            subject: String(body),
            viewLabel: t("notifications.view"),
            onView: () => {
              if (path) navigate(path);
              else navigate(listPath);
            },
          });
        });

        // Re-attach the device to whoever is signed in now, and heal the row if
        // the server pruned it after a delivery failure.
        if (ownerFlowPushNotification.isServerEnabled()) {
          await ownerFlowPushNotification.syncExistingSubscription();
        }
      } catch {
        // Push may be off on this site; keep the app usable.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser, listPath, navigate, ownerPortal, t]);

  return null;
}
